import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "@/lib/content";

// Seiten (eigene Routen) und Abschnitte auf der Startseite.
const PAGE_LINKS = [
  { to: "/hilfe", label: "Braucht Hilfe?" },
  { to: "/tipps", label: "Garten & Futter" },
];
const SECTION_LINKS = [
  { to: "/#ueber-mich", label: "Über mich" },
  { to: "/#kontakt", label: "Kontakt" },
  { to: "/#spenden", label: "Spenden" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkBase =
    "text-base font-semibold text-ink-muted hover:text-brand-green transition-colors duration-300 relative group";

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl shadow-soft" : "bg-transparent"
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex flex-col leading-none">
          <span className="font-accent text-3xl md:text-4xl text-brand-green">
            IgelGemurmel
          </span>
          <span className="font-accent text-lg text-brand-pink -mt-1">
            Igelstation mit Herz
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {PAGE_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "text-brand-green" : ""}`
              }
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-pink transition-all duration-300 group-hover:w-full" />
            </NavLink>
          ))}
          {SECTION_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/#", "")}`}
              className={linkBase}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-pink transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </div>

        <Link
          to="/#kontakt"
          data-testid="nav-notfall-btn"
          className="hidden lg:inline-flex items-center gap-2 rounded-full bg-brand-pink px-6 py-3 text-white font-semibold shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#cf5a78]"
        >
          <Phone className="w-4 h-4" /> Notfall
        </Link>

        <button
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 text-ink"
          data-testid="mobile-menu-toggle"
          aria-label="Menü"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-ink/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {[...PAGE_LINKS, ...SECTION_LINKS].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="text-lg font-semibold text-ink"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={CONTACT.phoneHref}
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-brand-pink px-6 py-3 text-white font-semibold w-fit"
              >
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
